import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models/app-state.interface';
import { RecommendationInterface } from 'app/recommendation/models/recommendation.model';
import { sortEmployeeRecommendationsByProp } from 'app/shared/utilities/object-sort-methods.utility';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';

import { RecommendationTemplateInterface, RecommendationTemplateRuleInterface } from '../models';
import { AssessmentQuestionDataInterface } from '../models/assessment-question-data.model';
import { RecommendationCategoryInterface } from '../models/recommendation-category.model';
import {
  CreateRecommendationCategory,
  CreateRecommendationTemplate,
  CreateRecommendationTemplateRule,
  DeleteEmployeeRecommendation,
  DeleteRecommendationCategory,
  DeleteRecommendationTemplate,
  DeleteRecommendationTemplateRule,
  LoadAssessmentQuestions,
  LoadEmployeeRecommendations,
  LoadRecommendationsLibrary,
  UpdateEmployeeRecommendation,
  UpdateRecommendationCategory,
  UpdateRecommendationTemplate,
  UpdateRecommendationTemplateRule,
} from '../state/actions';

import { RecommendationServiceInterface } from './recommendation-service.interface';

export class RecommendationServiceFilters {

  public subjectId?: number;

  constructor({
    subjectId = undefined,
  }: RecommendationServiceFilters) {
    this.subjectId = subjectId;
  }
}

@Injectable()
export class RecommendationService implements RecommendationServiceInterface {
  private completedRecommendations$: BehaviorSubject<any> = new BehaviorSubject(null);

  constructor(private readonly _store: Store<AppStateInterface>, private _httpClient: HttpClient) {
    this.onInit();
  }

  public onInit(): void { }

  public findAllRecommendationsCompletedByUserId(userId: Readonly<number>,
                                                 reloadTasks: boolean = false): Observable<Readonly<RecommendationInterface[]>> {

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.recommendations)
      .pipe(
        map((entities: { [id: number]: RecommendationInterface }) => {

          let recommendation: RecommendationInterface = undefined;
          const recommendations: RecommendationInterface[] = [];
          for (const key in entities) {
            // skip loop if the property is from prototype
            if (!entities.hasOwnProperty(key)) { continue; }

            recommendation = entities[key];
            if (recommendation.subject.id != 0) {
              if (recommendation.isComplete && recommendation.subject && (recommendation.subject.id === userId)) {
                recommendations.push(recommendation);
              }
            } else {
              if (recommendation.isComplete && recommendation.subject) {
                recommendations.push(recommendation);
              }
            }

          }

          return sortEmployeeRecommendationsByProp(recommendations, 'recommendationDate');
        }),
      );
  }

  public findAllOpenRecommendationsThatApplyToUserId(userId: Readonly<number>,
                                                     reloadTasks: boolean = false): Observable<Readonly<RecommendationInterface[]>> {

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.recommendations)
      .pipe(
        map((entities: { [id: number]: RecommendationInterface }) => {

          let recommendation: RecommendationInterface = undefined;
          const recommendations: RecommendationInterface[] = [];
          for (const key in entities) {
            // skip loop if the property is from prototype
            if (!entities.hasOwnProperty(key)) { continue; }

            recommendation = entities[key];
            if (!recommendation.isComplete && recommendation.subject && (recommendation.subject.id === userId)) {
              recommendations.push(recommendation);
            }
          }

          return recommendations;
        }),
      );
  }

  public loadCompletedRecommendationsForUser(userId): Observable<RecommendationInterface[]> {
    return this._httpClient.get<RecommendationInterface[]>(
      `/api/users/${userId}/recommendations`,
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<RecommendationInterface[]>) =>

         sortEmployeeRecommendationsByProp(httpResponse.body, 'recommendationDate'),
        ));
  }

  public getMarkAsCompletedEvent(): Observable<any> {
    return this.completedRecommendations$.asObservable();
  }

  public setRecommendationValue(value: boolean) {
    this.completedRecommendations$.next(value);
  }

  public findOneById(id: number): Observable<Readonly<RecommendationInterface>> {
    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.recommendations[id]);
  }

  public findSelectedRecommendationId(): Observable<Readonly<number>> {
    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.selectedRecommendation);
  }

  public loadAll(filters?: RecommendationServiceFilters): void {
    this._store.dispatch(new LoadEmployeeRecommendations(filters));
  }

  public getRecommendationTemplatesLibrary(): Observable<Readonly<RecommendationCategoryInterface[]>> {
    this._store.dispatch(new LoadRecommendationsLibrary());

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.recommendationLibrary);
  }

  public loadAssessmentQuestionData(): void {
    this._store.dispatch(new LoadAssessmentQuestions());
  }

  public getAssessmentQuestionData(): Observable<Readonly<AssessmentQuestionDataInterface>> {
    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.assessmentQuestionData);
  }

  public update(recommendationToUpdate: RecommendationInterface): Observable<RecommendationInterface> {
    this._store.dispatch(new UpdateEmployeeRecommendation(recommendationToUpdate));

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.recommendations[recommendationToUpdate.id]);
  }

  public deleteRecommendationById(recommendationToDelete: RecommendationInterface): void {
    this._store.dispatch(new DeleteEmployeeRecommendation(recommendationToDelete));
  }

  public createRecommendationCategory(categoryData: RecommendationCategoryInterface): void {
    this._store.dispatch(new CreateRecommendationCategory(categoryData));
  }

  public updateRecommendationCategory(categoryData: RecommendationCategoryInterface): void {
    this._store.dispatch(new UpdateRecommendationCategory(categoryData));
  }

  public deleteRecommendationCategory(categoryData: RecommendationCategoryInterface): void {
    this._store.dispatch(new DeleteRecommendationCategory(categoryData));
  }

  public createTemplate(recommendation: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    this._store.dispatch(new CreateRecommendationTemplate(recommendation));

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.selectedLibraryItem);
  }

  public updateTemplate(recommendation: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    this._store.dispatch(new UpdateRecommendationTemplate(recommendation));

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.selectedLibraryItem);
  }

  public deleteTemplate(recommendation: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    this._store.dispatch(new DeleteRecommendationTemplate(recommendation));

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.selectedLibraryItem);
  }

  public addRuleToTemplate(rule: RecommendationTemplateRuleInterface,
                           recommendation: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    this._store.dispatch(new CreateRecommendationTemplateRule({ recommendation, rule }));

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.selectedLibraryItem);
  }

  public updateRuleToTemplate(rule: RecommendationTemplateRuleInterface,
                              recommendation: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    this._store.dispatch(new UpdateRecommendationTemplateRule({ recommendation, rule }));

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.selectedLibraryItem);
  }

  public deleteRuleFromTemplate(rule: RecommendationTemplateRuleInterface,
                                recommendation: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    this._store.dispatch(new DeleteRecommendationTemplateRule({ recommendation, rule }));

    return this._store.select(({ recommendation }: AppStateInterface) => recommendation.selectedLibraryItem);
  }
}
